import { OperandType } from '@acceligentllc/shared/enums/operand';
import { OperationType } from '@acceligentllc/shared/enums/operation';
import * as ReportBlockFieldEnum from '@acceligentllc/shared/enums/reportBlockField';
import { ReportBlockType } from '@acceligentllc/shared/enums/reportBlockType';

import type {
	ReportTypeBlockFormModel,
	ReportBlockFormModel,
	ReportBlockFieldFormModel,
} from '../../Shared/formModel';

enum ValidationErrorType {
	REPORT_TYPE = 'REPORT_TYPE',
	BLOCK = 'BLOCK',
	FIELD = 'FIELD',
}

export interface ValidationError {
	type: ValidationErrorType;
	message: string;
	fieldId?: string;
	blockId?: string;
}

interface ValidationResult {
	isValid: boolean;
	errors: ValidationError[];
}

/** Checks whether field names in block are unique but only for CALCULATED blocks */
const _areFieldNamesInBlockUnique = (block: ReportBlockFormModel, reportFieldsByIdMap: { [id: string]: ReportBlockFieldFormModel; }) => {
	if (block.type !== ReportBlockType.CALCULATED && block.type !== ReportBlockType.JOB_EHS) {
		return true;
	}

	const fieldNameMap: { [name: string]: true; } = {};

	for (const fieldId of (block.reportBlockFieldIds ?? [])) {
		const field = reportFieldsByIdMap[fieldId];

		if (!field) {
			continue;
		}

		if (fieldNameMap[field.name]) {
			return false;
		}

		fieldNameMap[field.name] = true;
	}

	return true;
};

const _getCalculatedFieldOperandUnitError = (
	field: ReportBlockFieldFormModel,
	reportFieldsByIdMap: { [id: string]: ReportBlockFieldFormModel; }
): Nullable<ValidationError> => {
	if (field.fieldType !== ReportBlockFieldEnum.Type.CALCULATED) {
		return null;
	}

	if (
		field.operationType === OperationType.COUNT
		|| field.operationType === OperationType.COUNT_EMPTY
		|| field.operationType === OperationType.COUNT_FILLED
	) {
		return null;
	}

	if (!field.unit) {
		return null;
	}

	const hasOperandWithDifferentUnit = (field.calculatedFieldOptions ?? []).some((option) => {
		if (option.type !== OperandType.FIELD || !option.id) {
			return false;
		}

		const operandField = reportFieldsByIdMap[option.id];

		if (!operandField?.unit) {
			return false;
		}

		return operandField.unit !== field.unit;
	});

	if (!hasOperandWithDifferentUnit) {
		return null;
	}

	return {
		type: ValidationErrorType.FIELD,
		fieldId: field.virtualId,
		blockId: field.reportBlockVirtualId,
		message: `Calculated field "${field.name}" has operands with a different unit than expected result.`,
	};
};

const _getCalculatedFieldUnitErrorsInBlock = (
	block: ReportBlockFormModel,
	reportFieldsByIdMap: { [id: string]: ReportBlockFieldFormModel; }
): ValidationError[] => {
	return (block.reportBlockFieldIds ?? []).reduce<ValidationError[]>((_acc, fieldId) => {
		const field = reportFieldsByIdMap[fieldId];

		if (!field) {
			return _acc;
		}

		const error = _getCalculatedFieldOperandUnitError(field, reportFieldsByIdMap);

		if (error) {
			_acc.push(error);
		}

		return _acc;
	}, []);
};

const _validateBlock = (
	block: ReportBlockFormModel,
	reportFieldsByIdMap: { [id: string]: ReportBlockFieldFormModel; }
): ValidationError[] => {
	const errors: ValidationError[] = [];

	if (!block.reportBlockFieldIds?.length) {
		errors.push({
			type: ValidationErrorType.BLOCK,
			blockId: block.virtualId,
			message: `Block "${block.name}" must contain at least one field.`,
		});

		return errors;
	}

	if (!_areFieldNamesInBlockUnique(block, reportFieldsByIdMap)) {
		errors.push({
			type: ValidationErrorType.BLOCK,
			blockId: block.virtualId,
			message: `Block "${block.name}" contains duplicate field names.`,
		});
	}

	errors.push(..._getCalculatedFieldUnitErrorsInBlock(block, reportFieldsByIdMap));

	return errors;
};

/**
 * Validates report type RM
 * Not Valid if:
 * 1. There is no primary main block
 * 2. There is no report type name
 * 3. There are blocks in secondary segment but no Main block in secondary segment
 * 4. If there exists a block without fields or if field names in block are not unique
 * 5. Field names in upper total block are not unique
 * 6. Field names in lower total block are not unique
 * 7. A calculated field has operands with different units
 */
export const validate = (
	primaryTypeMainBlockId: Nullable<string>,
	primaryTypeBlockIds: string[],
	secondaryTypeBlockIds: string[],
	secondaryTypeMainBlockId: Nullable<string>,
	upperTotalBlockId: Nullable<string>,
	lowerTotalBlockId: Nullable<string>,
	reportTypeBlocksByIdMap: { [id: string]: ReportTypeBlockFormModel; },
	reportBlocksByIdMap: { [id: string]: ReportBlockFormModel; },
	reportFieldsByIdMap: { [id: string]: ReportBlockFieldFormModel; },
	reportTypeName: string
): ValidationResult => {
	const errors: ValidationError[] = [];

	if (!primaryTypeMainBlockId) {
		errors.push({
			type: ValidationErrorType.REPORT_TYPE,
			message: 'Primary main block is required.',
		});
	}

	if (!reportTypeName?.length) {
		errors.push({
			type: ValidationErrorType.REPORT_TYPE,
			message: 'Report type name is required.',
		});
	}

	if (!!secondaryTypeBlockIds.length && !secondaryTypeMainBlockId) {
		errors.push({
			type: ValidationErrorType.REPORT_TYPE,
			message: 'Secondary main block is required when secondary blocks exist.',
		});
	}

	const regularBlockIds = [...(primaryTypeBlockIds ?? []), ...(secondaryTypeBlockIds ?? [])];

	for (const id of regularBlockIds) {
		const typeBlock = reportTypeBlocksByIdMap[id];

		if (!typeBlock) {
			continue;
		}

		const block = reportBlocksByIdMap[typeBlock.reportBlockVirtualId];

		if (!block) {
			continue;
		}

		errors.push(..._validateBlock(block, reportFieldsByIdMap));
	}

	if (upperTotalBlockId) {
		const upperTotalTypeBlock = reportTypeBlocksByIdMap[upperTotalBlockId];
		const upperTotalBlock = upperTotalTypeBlock
			? reportBlocksByIdMap[upperTotalTypeBlock.reportBlockVirtualId]
			: null;

		if (upperTotalBlock) {
			errors.push(..._validateBlock(upperTotalBlock, reportFieldsByIdMap));
		}
	}

	if (lowerTotalBlockId) {
		const lowerTotalTypeBlock = reportTypeBlocksByIdMap[lowerTotalBlockId];
		const lowerTotalBlock = lowerTotalTypeBlock
			? reportBlocksByIdMap[lowerTotalTypeBlock.reportBlockVirtualId]
			: null;

		if (lowerTotalBlock) {
			errors.push(..._validateBlock(lowerTotalBlock, reportFieldsByIdMap));
		}
	}

	return {
		isValid: errors.length === 0,
		errors,
	};
};
